import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { Wallet, ArrowLeft, Download, Mail, Loader2 } from 'lucide-react';

const Receipt = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await api.get(`/transactions/${reference}/receipt`);
        setReceipt(res.data.receipt);
      } catch {
        toast.error('Receipt not found');
        navigate('/transactions');
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [reference, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleEmailReceipt = async () => {
    setEmailing(true);
    try {
      await api.post(`/transactions/${reference}/email-receipt`);
      toast.success('Receipt sent to your email!');
    } catch {
      toast.error('Failed to send receipt');
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!receipt) return null;

  const isCredit = receipt.amount > 0;
  const sign = isCredit ? '+' : '-';
  const sym = receipt.currency_symbol || '$';
  const typeLabel = receipt.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const dateStr = receipt.date ? new Date(receipt.date).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';

  return (
    <div className="min-h-screen bg-muted px-4 py-8" data-testid="receipt-page">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6 text-sm"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </button>

        <div className="print:shadow-none print:border-none mb-4 flex gap-2 justify-end print:hidden">
          <Button variant="outline" size="sm" className="rounded-full" onClick={handlePrint} data-testid="download-receipt-button">
            <Download className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={handleEmailReceipt} disabled={emailing} data-testid="email-receipt-button">
            {emailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            {emailing ? 'Sending...' : 'Email Receipt'}
          </Button>
        </div>

        <Card className="border border-border/50 shadow-xl print:shadow-none" ref={receiptRef} data-testid="receipt-card">
          <CardContent className="pt-8 pb-8 px-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Wallet className="h-6 w-6 text-primary" />
                <span className="text-xl font-bold text-primary" style={{fontFamily: 'DM Sans'}}>SamaFlux</span>
              </div>
              <p className="text-sm text-muted-foreground">Transaction Receipt</p>
            </div>

            <div className="text-center mb-8">
              <p className={`text-4xl font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`} data-testid="receipt-amount">
                {sign}{sym}{Math.abs(receipt.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-muted-foreground mt-1 capitalize" data-testid="receipt-status">
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${receipt.status === 'success' ? 'bg-green-500' : receipt.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
                {receipt.status}
              </p>
            </div>

            <div className="border-t border-b border-border/50 py-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium" data-testid="receipt-type">{typeLabel}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Reference</span>
                <span className="font-mono text-xs" data-testid="receipt-reference">{receipt.reference}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium" data-testid="receipt-date">{dateStr}</span>
              </div>
              {receipt.description && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Description</span>
                  <span className="font-medium text-right max-w-[200px]" data-testid="receipt-description">{receipt.description}</span>
                </div>
              )}
              {receipt.recipient_email && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">To</span>
                  <span className="font-medium" data-testid="receipt-recipient">{receipt.recipient_email}</span>
                </div>
              )}
              {receipt.sender_email && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">From</span>
                  <span className="font-medium" data-testid="receipt-sender">{receipt.sender_email}</span>
                </div>
              )}
              {receipt.fee_amount > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount Sent</span>
                    <span className="font-medium" data-testid="receipt-principal">{sym}{receipt.principal_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fee</span>
                    <span className="font-medium text-amber-600" data-testid="receipt-fee">{sym}{receipt.fee_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-muted-foreground">Total Debited</span>
                    <span data-testid="receipt-total-debit">{sym}{receipt.total_debit?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">Account: {receipt.user.name}</p>
              <p className="text-xs text-muted-foreground">{receipt.user.email}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Receipt;
